import type { CSSProperties } from "react";
import type { KeyState } from "./keystate";

interface TileProps {
  letter: string;
  state: KeyState | null;
  /** Play the flip reveal; `index` staggers it. */
  reveal?: boolean;
  /** Play the win bounce (after the reveal). */
  bounce?: boolean;
  index?: number;
  /** Remount-driven pop when a letter is typed. */
  pop?: boolean;
}

const STATE_CLASS: Record<KeyState, string> = {
  g: "bg-tile-correct border-tile-correct text-white",
  y: "bg-tile-present border-tile-present text-white",
  b: "bg-tile-absent border-tile-absent text-white",
};

const STATE_VAR: Record<KeyState, string> = {
  g: "var(--tile-correct)",
  y: "var(--tile-present)",
  b: "var(--tile-absent)",
};

const REVEAL_STAGGER_MS = 300;
const REVEAL_TOTAL_MS = 4 * REVEAL_STAGGER_MS + 500;

export function Tile({ letter, state, reveal, bounce, index = 0, pop }: TileProps) {
  const base =
    "flex aspect-square w-full items-center justify-center border-2 text-[clamp(1.4rem,8vw,2.1rem)] font-bold uppercase leading-none";
  const colour = state
    ? STATE_CLASS[state]
    : letter
      ? "border-tile-borderActive"
      : "border-tile-border";

  const classes = [base, colour];
  const style: CSSProperties & Record<string, string | undefined> = {};

  if (reveal && state) {
    classes.push("tile-reveal");
    style["--reveal-color"] = STATE_VAR[state];
    style.animationDelay = `${index * REVEAL_STAGGER_MS}ms`;
  } else if (bounce) {
    classes.push("tile-bounce");
    style.animationDelay = `${REVEAL_TOTAL_MS + index * 100}ms`;
  } else if (pop) {
    classes.push("tile-pop");
  }

  return (
    <div className={classes.join(" ")} style={style}>
      {letter}
    </div>
  );
}

export { REVEAL_TOTAL_MS };
